import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  FailureScreenLayout,
  FailureScreenTitleWrapper,
} from "./assets/style";

import { Image } from "@nykaa/payment-ui/src/Image";
import { Title } from "@nykaa/payment-ui/src/Title";
import { SubTitle } from "@nykaa/payment-ui/src/SubTitle";
import { ProgressBar } from "@nykaa/payment-ui/src/ProgressBar";

import { getIcons, ICONS_NAMES } from "shared/helpers/utils/getIcons";
import { useTheme } from "emotion-theming";
import { getHexToRgba } from "@nykaa/payment-ui/src/common/helpers/getHexToRgba";
import { checkIsConvenienceChargesExperimentEnabled, getRemoteConfig } from "../../helpers";
import { getOrdersPaymentResponse, getPaymentConversionResponse } from "store/payment/services";
import { PAYMENT_STATUS } from "constants/paymentStatus";
import { CLIENT_URLS } from "constants/urls";
import { PAYMENT_GATEWAYS } from "constants/paymentGateways";
import { PAYMENT_MODES } from "constants/paymentModes";

import { ApplicationContext } from "wrapper/ApplicationWrapper/context";
import { UserContext } from "wrapper/UserWrapper/context";
import { ExperimentAndSwitchContext } from "wrapper/ExperimentAndSwitchWrapper/context";
import { getStringFromId } from "helpers/vernacular";
import { VERNACULAR_DEFAULT_MESSAGES, VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { appendQueryStringToUrl } from "helpers/utils/url";
import { BUY_NOW_FLOW_QUERY_PARAM } from "constants/index";
import { shouldAppendBuyNowApiParam } from "helpers/utils/buyNowCheckoutFlow";


export const InterMediateScreen = () => {
  const theme = useTheme() as Theme;
  const history = useHistory();
  const { AutoOrder } = useContext(ExperimentAndSwitchContext);
  const {
    applicationData: { deviceType },
  } = useContext(ApplicationContext);
  const { userData } = useContext(UserContext);
  const { isGuest } = userData;

  const query = new URLSearchParams(history.location.search);
  const orderId = query.get("orderId") as string;
  const autoOrderPageTitle = getStringFromId(
    VERNACULAR_STRING_IDS.AUTO_ORDER_PAGE_TITLE,
    VERNACULAR_DEFAULT_MESSAGES.AUTO_ORDER_PAGE_TITLE
  );
  const autoOrderPageSubTitle_1 = getStringFromId(
    VERNACULAR_STRING_IDS.AUTO_ORDER_PAGE_SUB_TITLE_1,
    VERNACULAR_DEFAULT_MESSAGES.AUTO_ORDER_PAGE_SUB_TITLE_1
  );
  const autoOrderPageSubTitle_2 = getStringFromId(
    VERNACULAR_STRING_IDS.AUTO_ORDER_PAGE_SUB_TITLE_2,
    VERNACULAR_DEFAULT_MESSAGES.AUTO_ORDER_PAGE_SUB_TITLE_2
  );


  const [autoOrderProgressBarFill, setAutoOrderProgressBarFill] = useState("0%");
  const getProgressBarAnimationTime = () => {
    try {
      const remoteConfig = getRemoteConfig();
      return remoteConfig.configs.payment.autoOrderAnimationTime || 2 ;
    } catch (error) {
      return 2;
    }
  };
  
  const getApiCallTime = ({
    timeAfterApiCall,
    timeBeforeApiCall,
  }: {
    timeAfterApiCall: number;
    timeBeforeApiCall: number;
  }) => {
    let redirectionTime = getProgressBarAnimationTime();
    //converting time in second from millisecond.
    let timeDifference = Math.floor((timeAfterApiCall - timeBeforeApiCall) / 1000);
    if (timeDifference > redirectionTime) {
      redirectionTime = (timeDifference - redirectionTime) * 1000;
    } else {
      redirectionTime = 1000;
    }

    return redirectionTime;
  };

  const handleAutoOrder = async (orderId: string) => {
    let redirectionTime = getProgressBarAnimationTime();

    setAutoOrderProgressBarFill("100%");
    if (!orderId) {
      let redirectionUrl = CLIENT_URLS.PAYMENT_BASE_URL;
      if (shouldAppendBuyNowApiParam()) {
        redirectionUrl = appendQueryStringToUrl(redirectionUrl, `${BUY_NOW_FLOW_QUERY_PARAM}=true`);
      }
      history.push(redirectionUrl);
      return;
    }
    const params = {
      orderId: orderId,
    };
    const dateBeforeApiCall = new Date();
    const timeBeforeApiCall = dateBeforeApiCall.getTime();

    let paymentResponseData = await getOrdersPaymentResponse(params);
    const paymentStatus = paymentResponseData?.paymentStatus;
    const autoOrderEligible = paymentResponseData?.autoOrderEligible;

    if (paymentStatus !== PAYMENT_STATUS.SUCCESS) {
      /**
       * if auto order is Eligible
       * then stop loader and show user
       * a placing cod order screen.
       */
      if (autoOrderEligible && AutoOrder.experimentEnabled) {
        let params: any = {
          paymentGateway: PAYMENT_GATEWAYS.COD,
          deviceType,
          mode: PAYMENT_MODES.COD,
          storeCardToken: false,
          walletAmount: 0,
          is_guest: Number(isGuest),
          paymentMetaDataTags: ["AUTO_COD"],
          autoOrderEligible: true,
          prevOrderId: orderId,
          applyCartCharges: checkIsConvenienceChargesExperimentEnabled(),
        };

        getPaymentConversionResponse(params)
          .then(data => {
            const newOrderId = data.orderData.orderId;
            const dateAfterApiCall = new Date();
            const timeAfterApiCall = dateAfterApiCall.getTime();
            redirectionTime = getApiCallTime({ timeBeforeApiCall, timeAfterApiCall });

            setTimeout(() => {
              // if order placed is successfull then redirect to oreder confirmation.
              if (data?.paymentStatus === PAYMENT_STATUS.SUCCESS) {
                const redirectUrl = `${CLIENT_URLS.PAYMENT_SUCCESS_PAGE}?orderId=${newOrderId}&autoOrder=true`;
                history.replace(redirectUrl);
              } else {
                const redirectUrl = `${CLIENT_URLS.PAYMENT_PRE_CONFIRMATION_PAGE}?orderId=${newOrderId}`;
                history.replace(redirectUrl);
              }
            }, redirectionTime);

          })
          .catch(() => {
            const dateAfterApiCall = new Date();
            const timeAfterApiCall = dateAfterApiCall.getTime();
            redirectionTime = getApiCallTime({ timeBeforeApiCall, timeAfterApiCall });
                        
            //redirect to preconfirmation page
            setTimeout(() => {
              const redirectUrl = `${CLIENT_URLS.PAYMENT_PRE_CONFIRMATION_PAGE}?orderId=${orderId}`;
              history.replace(redirectUrl);
            }, redirectionTime);

          })

        return;
      }
    } else {
      history.push(`${CLIENT_URLS.POST_PAYMENT_PAGE}?orderId=${orderId}`);
    }
  };

  useEffect(() => {
    handleAutoOrder(orderId);
  }, [orderId]);

  return (
    <FailureScreenLayout>
      <Image url={getIcons("", ICONS_NAMES.FAIL_ICON)} alt={"error"} theme={theme} />
      <FailureScreenTitleWrapper>
        <Title
          text={autoOrderPageTitle}
          font={theme?.typography?.titleSmall}
          color={theme.secondaryText}
          theme={theme}
          dataTestId="title_bottomsheet"
        />
        <SubTitle
          text={autoOrderPageSubTitle_1}
          font={theme.typography.bodyXlarge}
          theme={theme}
        />
        <SubTitle
          htmlText={autoOrderPageSubTitle_2}
          font={theme.typography.bodyXlarge}
          theme={theme}
        />
      </FailureScreenTitleWrapper>
      <ProgressBar
        positions={{
          top: "",
          bottom: "117px",
          left: "",
          right: "",
        }}
        height={"6px"}
        borderRadius={"12px"}
        backgroundColor={getHexToRgba(theme.brandPrimary, 0.08)}
        fillColor={theme.brandPrimary}
        progressWidth={autoOrderProgressBarFill}
        animationTime={`${getProgressBarAnimationTime()+'s'}`}
      />
    </FailureScreenLayout>
  );
};
